import { InputAdornment, TextField } from '@mui/material';
import { MetricConfig } from '@strength/domain';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../../hooks/useToast';
import { validateKpiEntry } from './kpi-benchmarks-utils';

type KpiEditCellProps = {
  /** Bare numeric value shown in the input (no unit), or `` when empty. */
  initialValue: string;
  /** Unit rendered as a suffix (e.g. `ml/kg/min`); hidden when the cell is empty. */
  unit: string;
  config: MetricConfig;
  isEditing: boolean;
  cellId: string;
  ariaLabel: string;
  onFocus: (cellId: string) => void;
  onBlur: () => void;
  /**
   * Called with the parsed numeric value once a changed entry validates.
   * `onSuccess` restores focus after the record lands; `onError` is invoked
   * when the server rejects the record so the cell can revert and show its
   * inline error.
   */
  onRecord: (value: number, onSuccess: () => void, onError: () => void) => void;
  onEnter?: (shiftKeyPressed: boolean) => void;
};

/**
 * A single KPI grid cell. In view mode it shows the recorded value and unit in a
 * disabled field; in edit mode it accepts numeric-only input, validates it
 * inline (accessible error via `aria-invalid` + an `alert` helper text), and
 * records the parsed value on blur. Non-numeric / keyword input is rejected and
 * never recorded. A server-side rejection of a recorded value reverts the cell
 * to its pre-edit value and shows the same inline error.
 */
export const KpiEditCell = ({
  initialValue,
  unit,
  config,
  isEditing,
  cellId,
  ariaLabel,
  onFocus,
  onBlur,
  onRecord,
  onEnter,
}: KpiEditCellProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState<string | null>(null);
  const errorId = useId();

  // Reset when the recorded value changes (e.g. after a successful record).
  useEffect(() => {
    setValue(initialValue);
    setError(null);
  }, [initialValue]);

  const handleBlur = () => {
    onBlur();

    // Unchanged: nothing to record.
    if (value === initialValue) {
      setError(null);
      return;
    }

    // Empty entry cannot remove an existing value; revert without recording,
    // matching the 1RM/exercise benchmark grid's reject-and-revert behavior.
    if (value.trim() === '') {
      setValue(initialValue);
      setError(null);
      toast({
        title: t('invalidValue'),
        description: t('cannotRemoveBenchmarkValue'),
        severity: 'error',
      });
      return;
    }

    const result = validateKpiEntry(value, config);
    if (!result.ok) {
      setError(t('onlyNumbersAllowed'));
      return;
    }

    setError(null);
    onRecord(
      result.value,
      () => undefined,
      // Server rejected the record (e.g. FORBIDDEN when the flag is off, or any
      // 4xx/5xx): revert to the pre-edit value and surface the same accessible
      // inline error (aria-invalid + role="alert") the client-validation path uses.
      () => {
        setValue(initialValue);
        setError(t('somethingWentWrong'));
      },
    );
  };

  const showUnit = unit !== '' && value !== '';

  return (
    <TextField
      variant="standard"
      error={!!error}
      inputProps={{
        'data-kpi-edit-input': true,
        'data-cell-id': cellId,
        'aria-label': ariaLabel,
        'aria-invalid': !!error,
        ...(error ? { 'aria-describedby': errorId } : {}),
      }}
      InputProps={{
        disableUnderline: true,
        sx: { backgroundColor: 'transparent' },
        endAdornment: showUnit ? (
          <InputAdornment position="end">{unit}</InputAdornment>
        ) : undefined,
      }}
      helperText={
        error ? (
          <span role="alert" id={errorId}>
            {error}
          </span>
        ) : undefined
      }
      disabled={!isEditing}
      value={value}
      onChange={(e) => {
        setValue(e.target.value);
        if (error) {
          setError(null);
        }
      }}
      onFocus={() => {
        if (isEditing) {
          onFocus(cellId);
        }
      }}
      onBlur={handleBlur}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          onEnter?.(e.shiftKey);
        }
      }}
    />
  );
};
